// @ts-nocheck
import cn from 'classnames';
import { flatten, isEmpty, uniq } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingMap } from 'sonata-platform-components';
import { Map as MapComponent, Type } from 'sonata-platform-components/lib/types/types/reportingMap.types';
import { Selector, SelectorOptionPropsInterface } from 'sonata-react-components';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';

import {
  reportAdvancedFilterSetCartographyLevel,
  reportAdvancedFilterSetCartographyLevelIds
} from '@/store/actions/reportingAdvancedFilter.actions';
import { getMapLoading, getReportingType, getSelectedCampaigns } from '@/store/selectors/reporting.selectors';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getCartographyDictionary } from '@/ts-resources/reporting.resources';
import { FootfallAttributionMapSelectorsStyled } from './FootfallAttributionMapSelectors.styled';

import type { GeoDictionaryTagsConfig } from 'mercator-cartography-api-client/lib/interfaces/geoDictionary.types';

type Props = {
  mapId: string;
};

type State = {
  cartographyLevelOptions: GeoDictionaryTagsConfig[];
  selectedMetricOption: SelectorOptionPropsInterface;
};

function FootfallAttributionMapSelectors({ mapId }: Props) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const token: Record<string, any> = {};
  const isDooh = isDOOHCampaign(useSelector(getReportingType));
  // @ts-ignore
  const isLoading = useSelector(store => getMapLoading(store, mapId));

  const reportingCampaigns: Record<string, any>[] = useSelector(getSelectedCampaigns);
  const selectedCampaignIds = useSelector(getCampaigns);

  const { getMap, getMaps, resetMapCartographyFilter, setMapCartographySettings, setMapSettings, setMapType } =
    useReportingMap();
  const map = getMap({ id: mapId });
  // @ts-ignore
  const shouldExpanded = getMaps().length === 2;

  const mapCampaigns = reportingCampaigns.filter(reportingCampaign =>
    selectedCampaignIds.includes(reportingCampaign.id)
  );
  const hasLegacyFootfallAttribution = mapCampaigns.some(
    campaign => campaign.hasNewAttributionStats && !campaign.hasDriveToStore
  );
  const isDriveToStore = mapCampaigns.some(campaign => campaign.hasDriveToStore);

  const metricsOptions: SelectorOptionPropsInterface[] = [];
  if (hasLegacyFootfallAttribution) {
    metricsOptions.push({
      checked: true,
      id: 'footfall_attribution',
      label: formatMessage({
        defaultMessage: 'Store Visits',
        id: 'reporting.metrics.footfall_attribution'
      }),
      value: 'footfall_attribution'
    });
  }
  if (isDriveToStore) {
    metricsOptions.push({
      checked: !hasLegacyFootfallAttribution,
      id: 'fa_venue_uplift',
      label: formatMessage({
        defaultMessage: 'Uplift',
        id: 'reporting.metrics.fa_venue_uplift'
      }),
      value: 'fa_venue_uplift'
    });
  }
  const primaryMetricIds = metricsOptions.map(option => option.id);

  if (map?.type === 'locations' || map?.type === 'screens') {
    if (hasLegacyFootfallAttribution) {
      metricsOptions.push(
        {
          id: 'footfall_attribution_rate',
          label: formatMessage({
            defaultMessage: 'V. Rate',
            id: 'reporting.metrics.footfall_attribution_rate'
          }),
          value: 'footfall_attribution_rate'
        },
        {
          id: 'cpfootfall_attribution',
          label: formatMessage({
            defaultMessage: 'V. Cost',
            id: 'reporting.metrics.cp_footfall_attribution'
          }),
          value: 'cpfootfall_attribution'
        }
      );
    }
    if (isDriveToStore) {
      metricsOptions.push(
        {
          id: 'fa_venue_uplift_pct',
          label: formatMessage({
            defaultMessage: 'Uplift %',
            id: 'reporting.metrics.fa_venue_uplift_pct'
          }),
          value: 'fa_venue_uplift_pct'
        },
        {
          id: 'fa_venue_cost_per_visit',
          label: formatMessage({
            defaultMessage: 'CPV',
            id: 'reporting.metrics.fa_venue_cost_per_visit'
          }),
          value: 'fa_venue_cost_per_visit'
        },
        {
          id: 'fa_venue_roi',
          label: formatMessage({
            defaultMessage: 'ROI',
            id: 'reporting.metrics.fa_venue_roi'
          }),
          value: 'fa_venue_roi'
        },
        {
          id: 'fa_venue_total_spent',
          label: formatMessage({
            defaultMessage: 'Total Spent',
            id: 'reporting.metrics.fa_venue_total_spent'
          }),
          value: 'fa_venue_total_spent'
        },
        {
          id: 'fa_venue_income',
          label: formatMessage({
            defaultMessage: 'Income',
            id: 'reporting.metrics.fa_venue_income'
          }),
          value: 'fa_venue_income'
        }
      );
    }
  }

  const dimensionOptions = [
    {
      id: 'stores',
      label: formatMessage({
        defaultMessage: 'Store',
        id: 'reporting.footfallAttributionMap.dimensionOptions.store'
      }),
      selected: true,
      value: 'stores'
    },
    {
      id: `${isDooh ? 'screens' : 'locations'}`,
      label: formatMessage({
        defaultMessage: `${isDooh ? 'Screen' : 'Location'}`,
        id: `reporting.footfallAttributionMap.dimensionOptions.${isDooh ? 'screen' : 'location'}`
      }),
      value: `${isDooh ? 'screens' : 'locations'}`
    }
  ];

  const defaultMetricOption = metricsOptions.filter(m => m.id === map?.settings[map?.type].events[0]);

  const [{ cartographyLevelOptions, selectedMetricOption }, setState] = useState<State>({
    cartographyLevelOptions: [],
    selectedMetricOption: metricsOptions[0]
  });

  const getDimensionsOptions = (): SelectorOptionPropsInterface[] => {
    if (primaryMetricIds.includes(selectedMetricOption.id)) {
      return [
        ...dimensionOptions,
        ...cartographyLevelOptions.map(option => {
          return {
            id: option.id!,
            label: `Cartography > ${option.name}`,
            value: option.id!
          };
        })
      ];
    }
    return [dimensionOptions[1]];
  };

  const getDefaultDimensionOption = () => {
    return map?.type === 'cartography'
      ? getDimensionsOptions().filter(d => d.id === map?.settings.cartography.level)
      : getDimensionsOptions().filter(d => d.id === map?.type);
  };

  const getCountries = () => {
    const campaigns = reportingCampaigns.filter(reportingCampaign =>
      selectedCampaignIds.includes(reportingCampaign.id)
    );
    return uniq(flatten(campaigns.map(campaign => campaign.countries)));
  };

  const defaultCountry = getCountries()[0]?.toUpperCase() || '';

  const fetchCartographyLevelOptions = async (requestToken: any) => {
    try {
      const countries = getCountries();
      const countriesInfo = await getCartographyDictionary(countries, requestToken);
      const newOptions = countriesInfo[0].tagsConfig
        ?.filter(tag => {
          return (
            (countriesInfo.length === 1 && tag?.reportingMapEventsEnable) ||
            (countriesInfo.length > 1 && tag?.id === 'level0')
          );
        })
        .map(option => ({
          id: option?.id || 'N/A',
          name: option?.name || 'N/A'
        }));
      setState(prevState => ({
        ...prevState,
        cartographyLevelOptions: newOptions as GeoDictionaryTagsConfig[]
      }));
    } catch (e: any) {
      // biome-ignore lint/suspicious/noConsole: off
      console.info(e.message);
    }
  };

  useEffect(() => {
    fetchCartographyLevelOptions(token);
    return () => {
      token.cancel?.();
    };
  }, []);

  const handleMetricSelectorChange = (option: SelectorOptionPropsInterface) => {
    setState(prevState => ({
      ...prevState,
      selectedMetricOption: option
    }));
    if (map) {
      setMapSettings({
        id: mapId,
        value: {
          ...map.settings,
          [map?.type]: {
            ...map.settings[map?.type],
            events: [option.value]
          }
        }
      });
    }
  };

  const handleDimensionSelectorChange = (option: SelectorOptionPropsInterface) => {
    const mapType: MapComponent['type'] = !['locations', 'screens', 'stores'].includes(option.id.toString())
      ? 'cartography'
      : (option.id! as Type);
    if (map) {
      const isSameMapType = map?.type === mapType;
      if (isSameMapType) {
        if (mapType === 'cartography') {
          const isSameCartographyLevel = map?.settings?.[mapType]?.level === option.value;
          if (!isSameCartographyLevel) {
            dispatch(reportAdvancedFilterSetCartographyLevel(option.value, 'maps', mapId));
            dispatch(reportAdvancedFilterSetCartographyLevelIds([], 'maps', mapId));
            resetMapCartographyFilter({ id: mapId });
            setMapCartographySettings({
              id: mapId,
              value: {
                ...map.settings[mapType],
                countries: [defaultCountry],
                events: [selectedMetricOption.value!],
                level: option.value!
              }
            });
          }
        }
      } else {
        setMapSettings({
          id: mapId,
          value: {
            ...map.settings,
            [mapType]: {
              ...map.settings[mapType],
              events: [selectedMetricOption.value],
              ...(mapType === 'cartography' && {
                countries: [defaultCountry],
                level: option.value
              })
            }
          }
        });
        setMapType({
          id: mapId,
          value: mapType
        });
      }
    }
  };

  const isSelectorDimensionDisabled = primaryMetricIds.includes(selectedMetricOption.id)
    ? isEmpty(cartographyLevelOptions)
    : false;

  const footfallAttributionMapSelectors = cn('footfallAttributionMapSelectors__selectorsSection', {
    'footfallAttributionMapSelectors__selectorsSection--expanded': shouldExpanded
  });
  return (
    <FootfallAttributionMapSelectorsStyled>
      <div className={footfallAttributionMapSelectors}>
        <Selector
          className="footfallAttributionMapSelectors__metricSelector"
          defaultValue={defaultMetricOption}
          isDisabled={metricsOptions.length === 1}
          label={formatMessage({
            defaultMessage: 'Map view',
            id: 'reporting.footfallAttributionMap.mapSelectors.title'
          })}
          onChange={handleMetricSelectorChange}
          options={metricsOptions}
          type="simple"
        />
        <Selector
          className="footfallAttributionMapSelectors__dimensionSelector"
          isDisabled={isSelectorDimensionDisabled || isLoading}
          label={formatMessage({
            defaultMessage: 'By',
            id: 'reporting.footfallAttributionMap.mapSelectors.by'
          })}
          onChange={handleDimensionSelectorChange}
          options={getDimensionsOptions()}
          type="simple"
          value={getDefaultDimensionOption()}
        />
      </div>
    </FootfallAttributionMapSelectorsStyled>
  );
}

export default FootfallAttributionMapSelectors;
